import { db } from '../firebaseInit';
import {
  collection,
  doc,
  addDoc,
  getDocs,
  getDocsFromServer,
  getDocsFromCache,
  getDoc,
  query,
  orderBy,
  limit,
  serverTimestamp,
  writeBatch,
} from 'firebase/firestore';
import { getArtifactProjectId, getTemplatePath } from './firestorePaths';

const MAX_REVISIONS = 30;

function getRevisionsPath(teamId, templateId) {
  const appId = getArtifactProjectId();
  return `${getTemplatePath(appId, teamId, templateId)}/revisions`;
}

/**
 * Save a revision snapshot of the current template state.
 * Call this BEFORE applying any changes.
 */
export async function saveRevision(teamId, templateId, templateData, {
  reason = 'Manual save',
  triggeredBy = 'user',
  userEmail = null,
} = {}) {
  try {
    const revisionsPath = getRevisionsPath(teamId, templateId);
    const revisionsRef = collection(db, revisionsPath);

    const revision = {
      graph: templateData.graph || {},
      steps: templateData.steps || null,
      prospectForm: templateData.prospectForm || null,
      templateName: templateData.name || 'Untitled',
      reason,
      triggeredBy,
      userEmail,
      savedAt: serverTimestamp(),
    };

    const docRef = await addDoc(revisionsRef, revision);
    console.log(`✅ Revision saved: ${docRef.id} — ${reason}`);

    await pruneOldRevisions(revisionsPath);

    return { success: true, revisionId: docRef.id };
  } catch (error) {
    console.error('❌ Failed to save revision:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Load revision history for a template, newest first.
 */
export async function loadRevisions(teamId, templateId, maxResults = 20) {
  try {
    const revisionsPath = getRevisionsPath(teamId, templateId);
    const revisionsRef = collection(db, revisionsPath);
    const q = query(revisionsRef, orderBy('savedAt', 'desc'), limit(maxResults));

    // Try cache first (fast, ~400ms), then server with a hard 6s timeout.
    // clearTimeout prevents dangling rejected promises after the race settles.
    let cacheTimerId;
    const cacheTimeout = new Promise((_, reject) => {
      cacheTimerId = setTimeout(() => reject(new Error('cache timeout')), 400);
    });
    let snapshot;
    try {
      snapshot = await Promise.race([getDocsFromCache(q), cacheTimeout]);
      clearTimeout(cacheTimerId);
      // If cache is empty, fall through to server fetch — cache may just be cold.
      if (snapshot.empty) throw new Error('cache empty');
    } catch {
      clearTimeout(cacheTimerId);
      let serverTimerId;
      const serverTimeout = new Promise((_, reject) => {
        serverTimerId = setTimeout(() => reject(new Error('server timeout')), 6000);
      });
      try {
        snapshot = await Promise.race([getDocsFromServer(q), serverTimeout]);
        clearTimeout(serverTimerId);
      } catch (serverErr) {
        clearTimeout(serverTimerId);
        console.warn('⚠️ Revision history unavailable:', serverErr.message);
        return [];
      }
    }

    return snapshot.docs.map((d) => ({
      id: d.id,
      ...d.data(),
      savedAt: d.data().savedAt?.toDate?.() || null,
    }));
  } catch (error) {
    console.error('❌ Failed to load revisions:', error);
    return [];
  }
}

/**
 * Load a single revision by ID.
 */
export async function loadRevision(teamId, templateId, revisionId) {
  try {
    const revisionsPath = getRevisionsPath(teamId, templateId);
    const revisionRef = doc(db, revisionsPath, revisionId);
    const snapshot = await getDoc(revisionRef);

    if (!snapshot.exists()) return null;

    return {
      id: snapshot.id,
      ...snapshot.data(),
      savedAt: snapshot.data().savedAt?.toDate?.() || null,
    };
  } catch (error) {
    console.error('❌ Failed to load revision:', error);
    return null;
  }
}

/**
 * Keep only the most recent MAX_REVISIONS. Delete the rest.
 */
async function pruneOldRevisions(revisionsPath) {
  try {
    const revisionsRef = collection(db, revisionsPath);
    const q = query(revisionsRef, orderBy('savedAt', 'desc'));
    const snapshot = await getDocs(q);

    if (snapshot.size <= MAX_REVISIONS) return;

    const batch = writeBatch(db);
    const docsToDelete = snapshot.docs.slice(MAX_REVISIONS);
    docsToDelete.forEach((d) => batch.delete(d.ref));
    await batch.commit();

    console.log(`🗑️ Pruned ${docsToDelete.length} old revisions`);
  } catch (error) {
    console.warn('⚠️ Failed to prune old revisions:', error);
  }
}
